// OWNER: FRONTEND-ADMIN. Pure helpers for the Numbers page (SPEC §6, §15.7): which status changes a number allows,
// the row's Manage menu, and the one-line-each registration summary. No DOM, so tests can import it.
import type { NumberStatus, OurNumber } from '../../../src/shared/api';
import { isoToYmd, shortYmd } from './dates';

export interface StatusAction { to: NumberStatus; label: string; danger?: boolean; help: string }

/** Status changes an admin may make (each needs a ledger note). Nothing here ever releases a number from Twilio. */
export function statusActions(n: Pick<OurNumber, 'status'>): StatusAction[] {
  const out: StatusAction[] = [];
  if (n.status === 'ramping' || n.status === 'active') out.push({ to: 'resting', label: 'Rest', help: 'Stops outbound dials from this number until it is reactivated. Inbound callbacks still ring.' });
  if (n.status !== 'flagged' && n.status !== 'retired') out.push({ to: 'flagged', label: 'Flag', danger: true, help: 'Marks the number as labelled/suspect. It stops placing calls. Remediate it (Hiya, Free Caller Registry, TNS) before reactivating; replace it one-for-one only after two failed remediation cycles.' });
  if (n.status === 'resting' || n.status === 'flagged') out.push({ to: 'active', label: 'Reactivate', help: 'Puts the number back into the dialling pool at its ramp cap.' });
  if (n.status !== 'retired') out.push({ to: 'retired', label: 'Retire', danger: true, help: 'Takes the number out of the pool for good. It is NOT released from Twilio: it keeps ringing through for callbacks from past prospects.' });
  return out;
}

export type NumberMenuItem =
  | { kind: 'ledger'; label: string }
  | { kind: 'edit'; label: string }
  | { kind: 'status'; label: string; action: StatusAction; danger?: boolean };

/** The row's Manage menu: ledger and edit, then every status change the number allows (nothing hidden off-screen). */
export function numberMenu(n: Pick<OurNumber, 'status'>): NumberMenuItem[] {
  return [
    { kind: 'ledger', label: 'Ledger, tests and daily stats…' },
    { kind: 'edit', label: 'Edit metro and registrations…' },
    ...statusActions(n).map((a) => ({ kind: 'status' as const, label: `${a.label}…`, action: a, danger: a.danger })),
  ];
}

/** The quick actions pinned to a health alert: Flag and Rest when the number allows them. */
export function alertActions(n: Pick<OurNumber, 'status'>): StatusAction[] {
  return statusActions(n).filter((a) => a.to === 'flagged' || a.to === 'resting');
}

export interface RegistrationLine { name: 'CNAM' | 'FCR' | 'Hiya'; state: 'done' | 'missing' | 'n/a'; text: string; title: string }

/** One line per registration, with its date: CNAM (US only; no date is stored), Free Caller Registry, Hiya. */
export function registrationLines(n: Pick<OurNumber, 'country' | 'cnamSet' | 'fcrRegisteredAt' | 'hiyaRegisteredAt'>, today: string): RegistrationLine[] {
  const dated = (name: 'FCR' | 'Hiya', long: string, iso: string | null): RegistrationLine => {
    const ymd = isoToYmd(iso);
    return ymd
      ? { name, state: 'done', text: `${name} ${shortYmd(ymd, today)}`, title: `${long}: registered ${ymd}` }
      : { name, state: 'missing', text: `${name} no`, title: `${long}: not registered yet` };
  };
  return [
    n.country === 'CA'
      ? { name: 'CNAM', state: 'n/a', text: 'CNAM n/a', title: 'CNAM: Twilio sets caller name on US numbers only' }
      : n.cnamSet
        ? { name: 'CNAM', state: 'done', text: 'CNAM set', title: 'CNAM: caller name set (VALISTIX)' }
        : { name: 'CNAM', state: 'missing', text: 'CNAM no', title: 'CNAM: caller name not set yet' },
    dated('FCR', 'Free Caller Registry', n.fcrRegisteredAt),
    dated('Hiya', 'Hiya', n.hiyaRegisteredAt),
  ];
}

// ---------- STIR/SHAKEN attestation (T1 self-test, RUNBOOK §2.4) ----------

/** How to produce the reading: TELEPHONY's inbound webhook records StirVerstat when one of our numbers calls the main one. */
export const SELF_TEST_HINT = 'Call our main number from this number to test';

export interface AttestationReading { passed: boolean; grade: 'A' | 'B' | 'C' | null }

/** Twilio StirVerstat → pass/fail and grade. 'TN-Validation-Passed-A' → A passed; 'TN-Validation-Failed-B',
 *  'No-TN-Validation' → failed. A bare 'A'/'B'/'C' counts as passed. Anything else is not a pass (default deny). */
export function readAttestation(level: string | null | undefined): AttestationReading {
  const s = (level ?? '').trim();
  const letter = /(?:^|[-_\s])([abc])$/i.exec(s)?.[1]?.toUpperCase() as 'A' | 'B' | 'C' | undefined;
  if (!s || /fail|no-?tn|unverified|invalid/i.test(s)) return { passed: false, grade: letter ?? null };
  if (/passed/i.test(s) || /^[abc]$/i.test(s)) return { passed: !!letter, grade: letter ?? null };
  return { passed: false, grade: null };
}

export interface AttestationLine { state: 'done' | 'warn' | 'bad' | 'n/a'; text: string; title: string }

/** The Caller ID column's last line: green for a passed A, amber for B/C, red for none or failed, grey when the server
 *  doesn't report attestation at all (the field is missing, not null). */
export function attestationLine(n: Pick<OurNumber, 'lastAttestation'> & Partial<Pick<OurNumber, 'purpose'>>, today: string, mainNumber?: string | null): AttestationLine {
  const how = mainNumber ? `${SELF_TEST_HINT} (${mainNumber}).` : `${SELF_TEST_HINT}.`;
  if (n.purpose === 'main') return { state: 'n/a', text: 'STIR n/a', title: 'The main number never places outbound calls, so its attestation is not tested.' };
  if (n.lastAttestation === undefined) {
    return { state: 'n/a', text: 'STIR n/a', title: `STIR/SHAKEN attestation isn't reported by this server yet (TELEPHONY adds OurNumber.lastAttestation). ${how}` };
  }
  const a = n.lastAttestation;
  if (!a || !a.level) return { state: 'bad', text: 'STIR none', title: `No self-test call recorded, so the STIR/SHAKEN attestation is unknown. ${how}` };
  const ymd = isoToYmd(a.at);
  const when = ymd ? ` ${shortYmd(ymd, today)}` : '';
  const r = readAttestation(a.level);
  const base = `STIR/SHAKEN attestation from the last self-test call: ${a.level}${ymd ? ` on ${ymd}` : ''}.`;
  if (r.passed && r.grade === 'A') return { state: 'done', text: `STIR A${when}`, title: `${base} Full attestation: carriers see a verified caller. ${how}` };
  if (r.passed && r.grade) {
    return { state: 'warn', text: `STIR ${r.grade}${when}`, title: `${base} Only ${r.grade === 'B' ? 'partial' : 'gateway'} attestation: carriers trust the call less and label it more often. Check that the number is assigned to the approved SHAKEN/STIR profile in Twilio Trust Hub, then test again. ${how}` };
  }
  const failed = /fail|no-?tn|unverified|invalid/i.test(a.level);
  return { state: 'bad', text: `STIR ${failed ? 'failed' : '?'}${when}`, title: `${base} ${failed ? 'Not a pass' : 'Not a reading the dialer knows, so it is not treated as a pass'}: carriers may label or block calls from this number. ${how}` };
}
